"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ChevronRight, Pin } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/useStore";
import { getInformationList } from "@/store/controller/information.controller";
import { formatDate } from "./format";
import { getCategoryColor } from "./theme";

export function BeritaPengumumanSection() {
  const dispatch = useAppDispatch();
  const information = useAppSelector((state) => state.information.list);
  const [activeTab, setActiveTab] = useState<"berita" | "pengumuman">("berita");

  useEffect(() => {
    dispatch(getInformationList({}));
  }, [dispatch]);

  const news = information.filter((item) => item.type === "berita");
  const announcements = information.filter((item) => item.type === "pengumuman");

  return (
    <section id="berita" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <span className="inline-block text-green-700 font-semibold text-sm uppercase tracking-widest mb-3">
              Informasi Terkini
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
              Berita & <span className="text-green-700">Pengumuman</span>
            </h2>
            <div className="w-14 h-1 bg-yellow-400 rounded mt-3" />
          </div>
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
            <button
              onClick={() => setActiveTab("berita")}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeTab === "berita" ? "bg-white shadow text-green-700" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Berita
            </button>
            <button
              onClick={() => setActiveTab("pengumuman")}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeTab === "pengumuman" ? "bg-white shadow text-green-700" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Pengumuman
            </button>
          </div>
        </div>

        {activeTab === "berita" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.map((newsItem) => (
              <Link
                key={newsItem.id}
                href={`/berita/${newsItem.id}`}
                className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="h-48 overflow-hidden bg-gray-100">
                  {newsItem.image && (
                    <img
                      src={newsItem.image}
                      alt={newsItem.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${getCategoryColor(newsItem.category_id)}`}>
                      {newsItem.category?.name}
                    </span>
                    <span className="text-gray-400 text-xs">{formatDate(newsItem.date)}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 leading-snug mb-2 text-sm group-hover:text-green-700 transition-colors">
                    {newsItem.title}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed line-clamp-2">{newsItem.excerpt || newsItem.content}</p>
                  <div className="mt-4 flex items-center gap-1 text-green-700 text-xs font-semibold group-hover:gap-2 transition-all">
                    Selengkapnya
                    <ChevronRight size={13} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div id="pengumuman" className="space-y-4">
            {announcements.map((announcement) => (
              <Link
                key={announcement.id}
                href={`/pengumuman/${announcement.id}`}
                className={`relative p-5 rounded-2xl border-l-4 flex items-start gap-4 hover:shadow-md transition-all cursor-pointer ${
                  announcement.pinned
                    ? "bg-orange-50 border-orange-500 border border-l-4"
                    : "bg-white border-green-400 border border-l-4"
                }`}
              >
                {announcement.pinned && (
                  <div className="absolute top-3 right-4 flex items-center gap-1 text-orange-600 text-xs font-bold">
                    <Pin size={12} />
                    Penting
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${getCategoryColor(announcement.category_id)}`}>
                      {announcement.category?.name}
                    </span>
                    <span className="text-gray-400 text-xs">{formatDate(announcement.date)}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">{announcement.title}</h3>
                  <p className="text-gray-500 text-sm">{announcement.excerpt || announcement.content}</p>
                </div>
                <ChevronRight size={20} className="text-gray-300 flex-shrink-0 mt-1" />
              </Link>
            ))}
          </div>
        )}

        <div className="text-center mt-8">
          <Link
            href={activeTab === "berita" ? "/berita" : "/pengumuman"}
            className="inline-flex items-center gap-2 text-green-700 border border-green-700 hover:bg-green-700 hover:text-white font-semibold px-6 py-3 rounded-xl transition-all text-sm"
          >
            Lihat Semua {activeTab === "berita" ? "Berita" : "Pengumuman"}
            <ChevronRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
